import supabaseClient from '@/api/supabaseClient.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { BackendErrorResponse } from '@/types/response/BackendErrorResponse';

export type UploadDocumentParams = {
  document_type: 'dbs' | 'insurance' | 'other';
  file: File;
};

const useUploadDocument = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (params: UploadDocumentParams) => {
    const { document_type, file } = params;

    // Get current user
    const { data: { user } } = await supabaseClient.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${document_type}_${Date.now()}.${fileExt}`;
    
    const { error: uploadError } = await supabaseClient.storage
      .from('documents')
      .upload(fileName, file);

    if (uploadError) {
      throw new Error(`Failed to upload file: ${uploadError.message}`);
    }

    // Create document record
    const { data, error } = await supabaseClient.functions.invoke('upload_document', {
      method: 'POST',
      body: {
        document_type,
        file_name: file.name,
        file_url: fileName,
      },
    });

    if (error) {
      // Clean up uploaded file if document creation fails
      await supabaseClient.storage.from('documents').remove([fileName]);
      
      if (error instanceof FunctionsHttpError) {
        const err: BackendErrorResponse = await error.context.json();
        throw new Error(err.errors.join(', '));
      }
      throw new Error((error as Error).message);
    }

    return data;
  };

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
  });
};

export default useUploadDocument;
